import type { ReactNode } from "react";
import { createContext, useCallback, useContext, useEffect, useState } from "react";

import { apiClient } from "../api/client.js";

interface VenueMembership {
  id: string;
  name: string;
  slug: string;
  role: string;
  fleet_id: string;
  fleet_name: string;
  address?: string;
  status: string;
  created_at: string;
}

interface UserProfile {
  id: string;
  email: string;
  display_name: string;
  is_platform_admin: boolean;
  venues: VenueMembership[];
}

interface AuthContextData {
  user: UserProfile | null;
  loading: boolean;
  accessDenied: boolean;
  selectedVenue: VenueMembership | null;
  setSelectedVenue: (venue: VenueMembership) => void;
  refresh: () => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextData>({
  user: null,
  loading: true,
  accessDenied: false,
  selectedVenue: null,
  setSelectedVenue: () => {},
  refresh: async () => {},
  logout: () => {},
});

export function useAuth() {
  return useContext(AuthContext);
}

const SELECTED_VENUE_STORAGE_KEY = "venue-portal:selected-venue-id";

function readStoredVenueId(): string | null {
  try {
    return localStorage.getItem(SELECTED_VENUE_STORAGE_KEY);
  } catch {
    return null;
  }
}

function resolveSelectedVenue(
  venues: VenueMembership[],
  preferredId: string | null,
): VenueMembership | null {
  if (venues.length === 0) return null;
  if (preferredId) {
    const match = venues.find((v) => v.id === preferredId);
    if (match) return match;
  }
  return venues[0];
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [accessDenied, setAccessDenied] = useState(false);
  const [selectedVenue, setSelectedVenueState] = useState<VenueMembership | null>(null);

  const setSelectedVenue = useCallback((venue: VenueMembership) => {
    setSelectedVenueState(venue);
    try {
      localStorage.setItem(SELECTED_VENUE_STORAGE_KEY, venue.id);
    } catch {
      /* private browsing / storage disabled */
    }
  }, []);

  const refresh = useCallback(async () => {
    try {
      const res = await apiClient["/api/v1/venue/auth/me"]["get"]({
        parameters: {},
        body: null,
      });
      if (res.code === 200) {
        const profile = res.body as UserProfile;
        setUser(profile);
        if (profile.venues.length === 0) {
          setAccessDenied(true);
        } else {
          setAccessDenied(false);
          setSelectedVenueState((prev) => {
            const storedId = readStoredVenueId();
            const preferredId = prev?.id ?? storedId;
            const next = resolveSelectedVenue(profile.venues, preferredId);
            if (next) {
              try {
                localStorage.setItem(SELECTED_VENUE_STORAGE_KEY, next.id);
              } catch {
                /* ignore */
              }
            }
            return next;
          });
        }
      } else if (res.code === 403) {
        setUser(null);
        setAccessDenied(true);
      } else {
        setUser(null);
        setAccessDenied(false);
      }
    } catch {
      setUser(null);
      setAccessDenied(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const logout = useCallback(async () => {
    try {
      await apiClient["/api/v1/venue/auth/logout"]["post"]({
        parameters: {},
        body: null,
      });
    } catch {
      // best-effort
    }
    setUser(null);
    setSelectedVenueState(null);
    try {
      localStorage.removeItem(SELECTED_VENUE_STORAGE_KEY);
    } catch {
      /* ignore */
    }
    setAccessDenied(false);
  }, []);

  return (
    <AuthContext.Provider
      value={{ user, loading, accessDenied, selectedVenue, setSelectedVenue, refresh, logout }}
    >
      {children}
    </AuthContext.Provider>
  );
}
